import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUrlState } from "@/lib/useUrlState";
import { SegmentedControl } from "@/components/ui";
import { ContentTopBar, TopBarTitle } from "@/components/layout/ContentTopBar";
import { Trophy } from "@/icons";
import { GlobalRank } from "@/components/rank/GlobalRank";
import { PrivateRank } from "@/components/rank/PrivateRank";
import { buildHead, SITE } from "@/lib/seo";

export const Route = createFileRoute("/_app/rank")({
  head: () =>
    buildHead({
      path: "/rank",
      title: "Leaderboard",
      description: `See who's predicting best on ${SITE.name} — global rankings and private leagues.`,
    }),
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({
    tab: s.tab === "private" ? "private" : undefined,
  }),
  component: RankPage,
});

function RankPage() {
  const { t } = useTranslation("rank");
  const [tab, setTab] = useUrlState<"global" | "private">("tab", "global");
  return (
    <div className="mx-auto w-full max-w-[640px] px-4 py-2 pb-10 lg:max-w-none lg:px-0 lg:pt-0">
      {/* Top bar spans the full content width on desktop (title left, bell +
          balance pinned top-right) — the -mx bleed reaches main's edges since
          the root goes full-width on lg, matching the Portfolio page. The pull-up
          is desktop-only (lg:-mt-4) so mobile keeps main's top padding under the
          Header instead of jamming the title against it; `divider` draws the
          full-bleed hairline below the bar (matches Home/Explore/Portfolio). */}
      <ContentTopBar className="-mx-4 mb-5 px-4 lg:-mx-6 lg:-mt-4 lg:px-6" divider>
        <TopBarTitle icon={<Trophy className="h-6 w-6 shrink-0" />}>{t("title")}</TopBarTitle>
      </ContentTopBar>
      {/* Body stays capped + centred at the shipped podium width (lg:max-w-6xl)
          so the podium/leaderboard keep their column even though the bar above
          now runs edge-to-edge. */}
      <div className="lg:mx-auto lg:max-w-6xl">
        <div className="mb-6 flex justify-center">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            segments={[
              { value: "global", label: t("tabs.global") },
              { value: "private", label: t("tabs.private") },
            ]}
          />
        </div>
        {tab === "private" ? <PrivateRank /> : <GlobalRank />}
      </div>
    </div>
  );
}
